'use client';

import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';
import Link from 'next/link';

const categories = [
  {
    title: 'Himalayan Expeditions',
    subtitle: '7–14 days · High altitude',
    description:
      'Full immersion into the high Himalayas. Passes above 4,500m, glacial valleys, ancient villages.',
    image: 'https://images.unsplash.com/photo-1655758956842-9c7c26ac5e81',
    alt: 'Trekkers on a high-altitude Himalayan ridge at dawn, snow-dusted peaks stretching to horizon, cold blue light',
    tag: 'Most Popular',
  },
  {
    title: 'Weekend Escapes',
    subtitle: '2–3 days · Accessible',
    description:
      'Recharge without taking a week off. Lush trails, forest camps, and mountain sunrises — within reach.',
    image: 'https://images.unsplash.com/photo-1606745977155-c78d7bd91465',
    alt: 'Green forested valley bathed in warm afternoon light, mist rising from valley floor, serene and open',
    tag: 'Quick Getaway',
  },
  {
    title: 'Adventure + Camping',
    subtitle: '4–6 days · Rugged',
    description:
      'Sleep under stars at 3,500m. River crossings, ridge camps, and terrain that demands respect.',
    image: 'https://images.unsplash.com/photo-1671465184864-1fe3ea6c7734',
    alt: 'Lone tent on a rocky mountain plateau at dusk, stars beginning to emerge, vast dark sky above',
    tag: 'Raw & Wild',
  },
  {
    title: 'Spiritual Treks',
    subtitle: '5–8 days · Reflective',
    description:
      'Ancient pilgrimage routes, sacred temples, and the kind of silence that changes you.',
    image: 'https://images.unsplash.com/photo-1611426663648-9856c9ee00a2',
    alt: 'Stone Buddhist stupa against dramatic mountain backdrop, prayer flags fluttering in wind, golden morning light',
    tag: 'Inner Journey',
  },
];

export default function ExperienceCategories() {
  const sectionRef = useRef<HTMLElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const imgRefs = useRef<(HTMLDivElement | null)[]>([]);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let ctx: { revert: () => void } | undefined;

    const init = async () => {
      const { gsap } = await import('gsap');
      const { ScrollTrigger } = await import('gsap/ScrollTrigger');
      gsap.registerPlugin(ScrollTrigger);

      ctx = gsap.context(() => {
        // ── HEADER REVEAL ─────────────────────────────────────────────
        gsap.fromTo(
          headerRef.current,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top 80%',
            },
          }
        );

        // ── HORIZONTAL SCROLL TRACK ───────────────────────────────────
        const cards = cardRefs.current.filter(Boolean);
        if (!trackRef.current || cards.length === 0) return;

        const totalWidth = trackRef.current.scrollWidth;
        const viewportWidth = window.innerWidth;
        const scrollDistance = totalWidth - viewportWidth + 120;

        gsap.to(trackRef.current, {
          x: -scrollDistance,
          ease: 'none',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top top',
            end: () => `+=${scrollDistance + viewportWidth * 0.5}`,
            pin: true,
            pinSpacing: true,
            scrub: 1.5,
            anticipatePin: 1,
          },
        });

        // ── PER-CARD PARALLAX IMAGES ──────────────────────────────────
        imgRefs.current.forEach((imgEl) => {
          if (!imgEl) return;
          gsap.fromTo(
            imgEl,
            { x: '-8%' },
            {
              x: '8%',
              ease: 'none',
              scrollTrigger: {
                trigger: sectionRef.current,
                start: 'top top',
                end: () => `+=${scrollDistance + viewportWidth * 0.5}`,
                scrub: 2,
              },
            }
          );
        });

        // ── CARD ENTRANCE STAGGER ─────────────────────────────────────
        cards.forEach((card, i) => {
          gsap.fromTo(
            card,
            { opacity: 0, y: 80, scale: 0.92 },
            {
              opacity: 1,
              y: 0,
              scale: 1,
              duration: 1,
              ease: 'power3.out',
              delay: i * 0.12,
              scrollTrigger: {
                trigger: sectionRef.current,
                start: 'top 85%',
              },
            }
          );
        });
      }, sectionRef);
    };

    init();
    return () => ctx?.revert();
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>, card: HTMLDivElement) => {
    const rect = card.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    card.style.transform = `perspective(900px) rotateY(${x * 10}deg) rotateX(${-y * 10}deg) translateY(-8px) scale(1.02)`;
  };

  const handleMouseLeave = (card: HTMLDivElement) => {
    card.style.transform =
      'perspective(900px) rotateY(0deg) rotateX(0deg) translateY(0px) scale(1)';
  };

  return (
    <section data-component-id="src/app/components/ExperienceCategories.tsx:158:4" data-component-path="src/app/components/ExperienceCategories.tsx" data-component-line="158" data-component-end-line="257" data-component-file="ExperienceCategories.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22id%22%3A%22experience%22%2C%22className%22%3A%22relative%20bg-white%20overflow-hidden%22%7D"
      ref={sectionRef}
      id="experience"
      className="relative bg-white overflow-hidden"
      style={{ minHeight: '100vh' }}
      aria-label="Experience categories"
    >
      {/* Section header — visible before scroll kicks in */}
      <div data-component-id="src/app/components/ExperienceCategories.tsx:166:6" data-component-path="src/app/components/ExperienceCategories.tsx" data-component-line="166" data-component-end-line="184" data-component-file="ExperienceCategories.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-6%20pt-32%20pb-16%22%7D"
        ref={headerRef}
        className="max-w-7xl mx-auto px-6 pt-32 pb-16"
        style={{ opacity: 0 }}
      >
        <div data-component-id="src/app/components/ExperienceCategories.tsx:171:8" data-component-path="src/app/components/ExperienceCategories.tsx" data-component-line="171" data-component-end-line="183" data-component-file="ExperienceCategories.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20md%3Aflex-row%20md%3Aitems-end%20md%3Ajustify-between%20gap-6%22%7D" className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div data-component-id="src/app/components/ExperienceCategories.tsx:172:10" data-component-path="src/app/components/ExperienceCategories.tsx" data-component-line="172" data-component-end-line="179" data-component-file="ExperienceCategories.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
            <span data-component-id="src/app/components/ExperienceCategories.tsx:173:12" data-component-path="src/app/components/ExperienceCategories.tsx" data-component-line="173" data-component-end-line="173" data-component-file="ExperienceCategories.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22section-tag%20block%20mb-4%22%2C%22textContent%22%3A%22Explore%22%7D" className="section-tag block mb-4">Explore</span>
            <h2 data-component-id="src/app/components/ExperienceCategories.tsx:174:12" data-component-path="src/app/components/ExperienceCategories.tsx" data-component-line="174" data-component-end-line="178" data-component-file="ExperienceCategories.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-4xl%20md%3Atext-5xl%20lg%3Atext-6xl%20font-bold%20tracking-tightest%20text-foreground%20leading-none%22%2C%22textContent%22%3A%22Choose%20Your%20Adventure%22%7D" className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tightest text-foreground leading-none">
              Choose Your
              <br data-component-id="src/app/components/ExperienceCategories.tsx:176:14" data-component-path="src/app/components/ExperienceCategories.tsx" data-component-line="176" data-component-end-line="176" data-component-file="ExperienceCategories.tsx" data-component-name="br" data-component-content="%7B%22elementName%22%3A%22br%22%7D" />
              Adventure
            </h2>
          </div>
          <p data-component-id="src/app/components/ExperienceCategories.tsx:180:10" data-component-path="src/app/components/ExperienceCategories.tsx" data-component-line="180" data-component-end-line="182" data-component-file="ExperienceCategories.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-muted-foreground%20max-w-xs%20leading-relaxed%20text-base%22%2C%22textContent%22%3A%22Every%20terrain.%20Every%20duration.%20Every%20kind%20of%20seeker%20%E2%80%94%20we%20have%20a%20trail%20for%20you.%22%7D" className="text-muted-foreground max-w-xs leading-relaxed text-base">
            Every terrain. Every duration. Every kind of seeker — we have a trail for you.
          </p>
        </div>
      </div>

      {/* Horizontal scroll track */}
      <div data-component-id="src/app/components/ExperienceCategories.tsx:187:6" data-component-path="src/app/components/ExperienceCategories.tsx" data-component-line="187" data-component-end-line="256" data-component-file="ExperienceCategories.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22overflow-hidden%20px-6%20pb-32%22%7D" className="overflow-hidden px-6 pb-32">
        <div data-component-id="src/app/components/ExperienceCategories.tsx:188:8" data-component-path="src/app/components/ExperienceCategories.tsx" data-component-line="188" data-component-end-line="255" data-component-file="ExperienceCategories.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20gap-6%20will-change-transform%22%7D"
          ref={trackRef}
          className="flex gap-6 will-change-transform"
          style={{ width: 'max-content' }}
        >
          {categories.map((cat, i) => (
            <div data-component-id="src/app/components/ExperienceCategories.tsx:194:12" data-component-path="src/app/components/ExperienceCategories.tsx" data-component-line="194" data-component-end-line="253" data-component-file="ExperienceCategories.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22card-tilt%20relative%20rounded-2xl%20overflow-hidden%20cursor-pointer%20flex-shrink-0%22%7D"
              key={cat.title}
              ref={(el) => { cardRefs.current[i] = el; }}
              className="card-tilt relative rounded-2xl overflow-hidden cursor-pointer flex-shrink-0"
              style={{
                width: 'clamp(320px, 38vw, 520px)',
                height: 'clamp(480px, 55vh, 640px)',
                opacity: 0,
                transition: 'transform 0.45s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.45s ease',
              }}
              onMouseMove={(e) => cardRefs.current[i] && handleMouseMove(e, cardRefs.current[i]!)}
              onMouseLeave={() => cardRefs.current[i] && handleMouseLeave(cardRefs.current[i]!)}
            >
              {/* Parallax image layer */}
              <div data-component-id="src/app/components/ExperienceCategories.tsx:208:14" data-component-path="src/app/components/ExperienceCategories.tsx" data-component-line="208" data-component-end-line="220" data-component-file="ExperienceCategories.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20will-change-transform%22%7D"
                ref={(el) => { imgRefs.current[i] = el; }}
                className="absolute will-change-transform"
                style={{ inset: '-10% -5%' }}
              >
                <AppImage data-component-id="src/app/components/ExperienceCategories.tsx:213:16" data-component-path="src/app/components/ExperienceCategories.tsx" data-component-line="213" data-component-end-line="219" data-component-file="ExperienceCategories.tsx" data-component-name="AppImage" data-component-content="%7B%22elementName%22%3A%22AppImage%22%2C%22className%22%3A%22object-cover%20transition-transform%20duration-700%20group-hover%3Ascale-%5B1.06%5D%22%7D"
                  src={cat.image}
                  alt={cat.alt}
                  fill
                  className="object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                  sizes="(max-width: 640px) 100vw, 40vw"
                />
              </div>

              {/* Gradient overlay */}
              <div data-component-id="src/app/components/ExperienceCategories.tsx:223:14" data-component-path="src/app/components/ExperienceCategories.tsx" data-component-line="223" data-component-end-line="223" data-component-file="ExperienceCategories.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20bg-gradient-to-t%20from-black%2F85%20via-black%2F20%20to-transparent%22%7D" className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />

              {/* Tag */}
              <div data-component-id="src/app/components/ExperienceCategories.tsx:226:14" data-component-path="src/app/components/ExperienceCategories.tsx" data-component-line="226" data-component-end-line="230" data-component-file="ExperienceCategories.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-5%20left-5%20z-10%22%7D" className="absolute top-5 left-5 z-10">
                <span data-component-id="src/app/components/ExperienceCategories.tsx:227:16" data-component-path="src/app/components/ExperienceCategories.tsx" data-component-line="227" data-component-end-line="229" data-component-file="ExperienceCategories.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22bg-white%2F10%20backdrop-blur-sm%20text-white%20text-xs%20font-semibold%20px-3%20py-1%20rounded-full%20border%20border-white%2F20%22%7D" className="bg-white/10 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1 rounded-full border border-white/20">
                  {cat.tag}
                </span>
              </div>

              {/* Content */}
              <div data-component-id="src/app/components/ExperienceCategories.tsx:233:14" data-component-path="src/app/components/ExperienceCategories.tsx" data-component-line="233" data-component-end-line="252" data-component-file="ExperienceCategories.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20bottom-0%20left-0%20right-0%20p-7%20z-10%22%7D" className="absolute bottom-0 left-0 right-0 p-7 z-10">
                <p data-component-id="src/app/components/ExperienceCategories.tsx:234:16" data-component-path="src/app/components/ExperienceCategories.tsx" data-component-line="234" data-component-end-line="236" data-component-file="ExperienceCategories.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-white%2F55%20text-xs%20tracking-widest%20uppercase%20mb-2%22%7D" className="text-white/55 text-xs tracking-widest uppercase mb-2">
                  {cat.subtitle}
                </p>
                <h3 data-component-id="src/app/components/ExperienceCategories.tsx:237:16" data-component-path="src/app/components/ExperienceCategories.tsx" data-component-line="237" data-component-end-line="239" data-component-file="ExperienceCategories.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-white%20text-2xl%20font-bold%20tracking-tight%20mb-2%20leading-snug%22%7D" className="text-white text-2xl font-bold tracking-tight mb-2 leading-snug">
                  {cat.title}
                </h3>
                <p data-component-id="src/app/components/ExperienceCategories.tsx:240:16" data-component-path="src/app/components/ExperienceCategories.tsx" data-component-line="240" data-component-end-line="242" data-component-file="ExperienceCategories.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-white%2F65%20text-sm%20leading-relaxed%20mb-5%20max-w-xs%22%7D" className="text-white/65 text-sm leading-relaxed mb-5 max-w-xs">
                  {cat.description}
                </p>
                <Link data-component-id="src/app/components/ExperienceCategories.tsx:243:16" data-component-path="src/app/components/ExperienceCategories.tsx" data-component-line="243" data-component-end-line="251" data-component-file="ExperienceCategories.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%23featured-treks%22%2C%22className%22%3A%22inline-flex%20items-center%20gap-2%20text-white%20text-sm%20font-semibold%20border-b%20border-white%2F40%20pb-0.5%20hover%3Aborder-white%20transition-colors%22%2C%22textContent%22%3A%22View%20Trips%22%7D"
                  href="#featured-treks"
                  className="inline-flex items-center gap-2 text-white text-sm font-semibold border-b border-white/40 pb-0.5 hover:border-white transition-colors"
                >
                  View Trips
                  <svg data-component-id="src/app/components/ExperienceCategories.tsx:248:18" data-component-path="src/app/components/ExperienceCategories.tsx" data-component-line="248" data-component-end-line="250" data-component-file="ExperienceCategories.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%7D" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                    <path data-component-id="src/app/components/ExperienceCategories.tsx:249:20" data-component-path="src/app/components/ExperienceCategories.tsx" data-component-line="249" data-component-end-line="249" data-component-file="ExperienceCategories.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
